import cx from 'classnames';
import { Fragment, useCallback, useState } from 'react';
import styles from '../Table.module.scss';
import { TableRowsProps } from './TableRows';
import RowExtension from './RowExtension';
import TableCell from './TableCell';
import useTableStore from '../store/useTableStore';
import LoadingRows from './LoadingRows';
import RowExtensionHandle from './RowExtensionHandle';
import { RowData } from '../utils/useRowHighlight';

type LoadedRowsProps<T> = Omit<Omit<TableRowsProps<T>, 'isLoading'>, 'columnCount'>;

const isRowDataRecord = (value: unknown): value is RowData => typeof value === 'object' && value !== null;

const getRowDataValue = (rowData: RowData, key: string | null | undefined): unknown => {
  if (!key) {
    return undefined;
  }

  return rowData[key];
};

const getRowDataStringValue = (rowData: RowData, key: string | null | undefined): string => {
  const value = getRowDataValue(rowData, key);

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  return '';
};

const getRowDataNumberValue = (rowData: RowData, key: string | null | undefined): number => {
  const value = getRowDataValue(rowData, key);

  if (typeof value === 'number') {
    return value;
  }

  const numericValue = Number(value ?? 0);

  return Number.isFinite(numericValue) ? numericValue : 0;
};

const hasRowDataValue = (value: unknown): boolean => value !== undefined && value !== null && value !== '';

const LoadedRows = <T,>({
  table,
  options,
  onToggleRow,
  rowExpandedDelayed,
  tableId,
  positionedColumnDefs,
  columnOffsets,
  searchHighlightValue,
}: LoadedRowsProps<T>) => {
  const isTableGrouped = table.getAllColumns().some(column => column.getIsGrouped());
  const tableState = useTableStore(state => state.state);
  const expandLoading = tableState[tableId].expandLoading;
  const expandCompareKey = tableState[tableId].dataPropertyForExpandComparison;
  const loadingRowCount = tableState[tableId].loadingRowCountKey;
  const highlightedField = tableState[tableId].highlightedField;
  const highlightedValue = tableState[tableId].highlightedValue;

  const isAnyRowLoading = Object.values(expandLoading).some(isLoading => isLoading);

  const [cornerCellWidth, setCornerCellWidth] = useState<number | undefined>(undefined);

  const cornerCellRefCallback = useCallback((node: HTMLTableCellElement | null) => {
    if (node) {
      setCornerCellWidth(node.getBoundingClientRect().width);
    }
  }, []);

  return (
    <>
      {[...table.getTopRows(), ...table.getCenterRows()].map((tableRow, index, rows) => {
        const cells = tableRow.getVisibleCells();
        const isLastSubRow = isTableGrouped && (rows[index + 1]?.depth || 0) < tableRow.depth;
        const visibleColumnsCount = cells?.length ?? 0;
        const rowData = isRowDataRecord(tableRow.original) ? tableRow.original : {};
        const isHighlighted = !!(highlightedField && highlightedValue);

        const rowClassnames = cx({
          [styles.rowPositionRelative]: true,
          [styles.selected]: tableRow.getIsSelected() ?? false,
          [styles.selectable]: tableRow.getCanSelect() ?? false,
          [styles.grouped]: tableRow.getCanExpand() ?? false,
          [styles.flatten]: !tableRow.getCanExpand() && tableRow.depth === 0,
          [styles.groupedExpanded]: tableRow.getIsExpanded() ?? false,
          [styles.lastSubRow]: isLastSubRow,
          [styles.flash]: isHighlighted,
        });

        const compareValue = getRowDataValue(rowData, expandCompareKey);
        const shouldDisplayExpandLoading =
          hasRowDataValue(compareValue) && expandLoading[String(compareValue)] && Number.isInteger(Number(tableRow.id));

        const loadingRowCountValue = getRowDataNumberValue(rowData, loadingRowCount);
        const highlightedFieldValue = highlightedField ? getRowDataValue(rowData, highlightedField) : undefined;

        const isRowExpandButtonDisabled = isAnyRowLoading && !tableRow.getIsExpanded() && table.getIsSomeRowsExpanded();
        return (
          <Fragment key={tableRow.id}>
            {!shouldDisplayExpandLoading && (
              <>
                <tr
                  id={`table-row-${getRowDataStringValue(rowData, 'interactionId')}`}
                  key={tableRow.id}
                  onClick={() => tableRow.toggleSelected()}
                  className={rowClassnames}
                  data-highlight-field={highlightedField || undefined}
                  data-highlight-value={highlightedFieldValue === undefined ? undefined : String(highlightedFieldValue)}
                >
                  {options?.verticalColumn && index === 0 && (
                    <td
                      ref={cornerCellRefCallback}
                      data-testid="cornerRowsLabel"
                      className={cx(styles.cornerRowsLabelCell, styles.sticky)}
                      rowSpan={options?.verticalColumn?.rowsStretched ? options?.verticalColumn?.rowsStretched + 1 : 1}
                      colSpan={1}
                    >
                      <div className={styles.cornerRowsLabel}>{options?.verticalColumn.text}</div>
                    </td>
                  )}
                  <RowExtensionHandle
                    row={tableRow}
                    rowExtensionEnabled={!!options?.rowExtension}
                    onToggleRow={toggledRow => {
                      onToggleRow(toggledRow);
                    }}
                    rowExpandedDelayed={rowExpandedDelayed}
                  />
                  {cells.map((cell, cellIndex) => (
                    <TableCell
                      key={cell.id}
                      cell={cell}
                      cellIndex={cellIndex}
                      options={options}
                      isRowExpandButtonDisabled={isRowExpandButtonDisabled}
                      positionedColumnDefs={positionedColumnDefs}
                      columnOffsets={columnOffsets}
                      searchHighlightValue={searchHighlightValue}
                      cornerCellWidth={cornerCellWidth}
                    />
                  ))}
                </tr>
              </>
            )}
            <RowExtension row={tableRow} options={options} rowExpandedDelayed={rowExpandedDelayed} />
            {shouldDisplayExpandLoading && (
              <LoadingRows pageSize={loadingRowCountValue} columnCount={visibleColumnsCount} />
            )}
          </Fragment>
        );
      })}
    </>
  );
};

export default LoadedRows;
